import {parseISO} from "date-fns";

import {
    EModuleLicenses,
    ESopLicenses,
    IDeviceFeaturesTooltipInfo,
    IDeviceLicenseInfo,
    IDeviceLicenseTooltip,
    IFeatureLicenseInfo,
    IFeatureTooltipInfo,
    IPartialDeviceLicenseInfo,
    ISimpleDeviceFeatures,
} from "@/components/license/models";
import license from "@/services/license";
import networkDevices from "@/services/network-devices";
import {IDeviceId, ILicensedDevice} from "@toolbox/models";
import {deviceName2Device} from "./device-check";

function isDeviceAPartOfDeviceB(deviceA: IDeviceId, deviceB: IDeviceId) {
    if (
        (networkDevices.isSeries(deviceB) &&
            networkDevices.sameSeries(deviceA, deviceB)) ||
        networkDevices.sameDevice(deviceA, deviceB)
    ) {
        return true;
    }

    return false;
}

function hasMatchingEntry(
    device: IDeviceId,
    includeSeries: boolean,
    featureLicenseInfos?: IFeatureLicenseInfo[],
) {
    if (!featureLicenseInfos) {
        return undefined;
    }

    return featureLicenseInfos.find(
        ({device: {designCode, deviceClass, series, fabrication}}) => {
            const deviceB = {
                designCode,
                deviceClass: deviceName2Device(deviceClass),
                series,
                fabrication,
            };
            return includeSeries
                ? isDeviceAPartOfDeviceB(device, deviceB)
                : networkDevices.sameDevice(device, deviceB);
        },
    );
}

export function getDeviceLicense(
    device: IDeviceId,
    simplified: false,
    includeSeries?: boolean,
): IDeviceLicenseInfo;
export function getDeviceLicense(
    device: IDeviceId,
    simplified: true,
    includeSeries?: boolean,
): ISimpleDeviceFeatures;
export function getDeviceLicense(
    device: IDeviceId,
    simplified: boolean,
    includeSeries: boolean = false,
): IDeviceLicenseInfo | ISimpleDeviceFeatures {
    const {modulesLicense, sopsLicense} = license.status;
    const modules: IPartialDeviceLicenseInfo<EModuleLicenses>[] = [];
    const simpleDeviceFeatures: ISimpleDeviceFeatures = [];
    if (modulesLicense) {
        for (const modlic of Object.values(EModuleLicenses)) {
            const moduleLicenseInfo = hasMatchingEntry(
                device,
                includeSeries,
                modulesLicense[modlic],
            );
            if (moduleLicenseInfo) {
                modules.push({
                    expiry: parseISO(moduleLicenseInfo.expiry),
                    feature: modlic,
                });
                simpleDeviceFeatures.push(modlic);
            }
        }
    }

    const sops: IPartialDeviceLicenseInfo<ESopLicenses>[] = [];
    if (sopsLicense) {
        for (const soplic of Object.values(ESopLicenses)) {
            const sopLicenseInfo = hasMatchingEntry(
                device,
                includeSeries,
                sopsLicense[soplic],
            );
            if (sopLicenseInfo) {
                sops.push({
                    expiry: parseISO(sopLicenseInfo.expiry),
                    feature: soplic,
                });
                simpleDeviceFeatures.push(soplic);
            }
        }
    }

    return simplified ? simpleDeviceFeatures : {modules, sops};
}

export function getDeviceFeatures(device?: IDeviceId): ISimpleDeviceFeatures {
    const {modules, sops} = license.status;
    return device
        ? getDeviceLicense(device, true, true)
        : [...modules, ...sops];
}

export function getDeviceTooltipInfo(
    device: ILicensedDevice,
    modulesLicense?: Record<
        EModuleLicenses | ESopLicenses,
        IFeatureLicenseInfo[]
    >,
    sopsLicense?: Record<EModuleLicenses | ESopLicenses, IFeatureLicenseInfo[]>,
): IDeviceLicenseTooltip {
    const getFeatures = (
        licenses?: Record<
            EModuleLicenses | ESopLicenses,
            IFeatureLicenseInfo[]
        >,
    ) =>
        Object.entries(licenses ?? {}).flatMap(([name, items]) =>
            (items ?? [])
                .filter(
                    (item) =>
                        item.device.deviceClass === device.deviceClass &&
                        item.device.series === device.series &&
                        item.device.designCode === device.designCode &&
                        item.device.fabrication === device.fabrication,
                )
                .map((item) => ({
                    feature: name as EModuleLicenses | ESopLicenses,
                    date: item.expiry,
                })),
        );

    return {
        modules: getFeatures(modulesLicense),
        sops: getFeatures(sopsLicense),
    };
}

export function getModuleTooltipInfo(
    module: EModuleLicenses,
    modulesLicense?: Record<
        EModuleLicenses | ESopLicenses,
        IFeatureLicenseInfo[]
    >,
): IFeatureTooltipInfo[] {
    return (modulesLicense?.[module] ?? []).map((item) => ({
        date: item.expiry,
        device: item.device,
    }));
}

export function getSopTooltipInfo(
    sop: ESopLicenses,
    sopsLicense?: Record<EModuleLicenses | ESopLicenses, IFeatureLicenseInfo[]>,
): IFeatureTooltipInfo[] {
    return (sopsLicense?.[sop] ?? []).map((item) => ({
        date: item.expiry,
        device: item.device,
    }));
}

export function groupByDate<
    TValue extends IFeatureTooltipInfo | IDeviceFeaturesTooltipInfo,
>(items: TValue[]) {
    const groups: Record<string, TValue[]> = {};

    items.forEach((item) => {
        const date = item.date;

        if (!groups[date]) {
            groups[date] = [];
        }

        groups[date].push(item);
    });

    // Sort dates in ascending order (oldest first)
    return Object.fromEntries(
        Object.entries(groups).sort(([dateA], [dateB]) => {
            return new Date(dateA).getTime() - new Date(dateB).getTime();
        }),
    );
}
